import React, { useState, useEffect, useCallback } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie, faCoins, faClockRotateLeft, faMagnifyingGlass, faBuilding, faArrowUp, faArrowDown, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiButton, UiModal, UiInput, UiFilterPanel } from '../../components/ui';
import DataTable from '../../components/shared/DataTable';
import type { Column } from '../../components/shared/DataTable';

interface FeeWalletRow {
  id: string;
  name: string;
  email: string;
  p2p_enabled?: boolean;
  fee_balance: number;
  total_credit: number;
  total_debit: number;
  payin_pct: number;
  payout_pct: number;
  // true = ตั้งเรท P2P ไว้เอง, false = fallback จาก fee ปกติ
  payin_pct_is_p2p?: boolean;
  payout_pct_is_p2p?: boolean;
}

interface LedgerRow {
  type: string;
  direction: string;
  amount: number;
  balance_after: number;
  ref_type?: string;
  note?: string;
  created_at: string;
}

const fmt = (n: any) => `฿${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const LEDGER_TYPE: Record<string, string> = {
  topup: 'เติมเครดิต', fee_payin: 'ค่าธรรมเนียมฝาก', fee_payout: 'ค่าธรรมเนียมถอน',
  adjustment: 'ปรับยอด', reversal: 'คืนยอด',
};

const AdminP2pOverviewPage: React.FC = () => {
  const [rows, setRows] = useState<FeeWalletRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [topupOf, setTopupOf] = useState<FeeWalletRow | null>(null);
  const [topupAmount, setTopupAmount] = useState('');

  const [ledgerOf, setLedgerOf] = useState<FeeWalletRow | null>(null);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));
      const res = await merchantApi.get(`/admin/deposit-orders/fee-wallets?${params.toString()}`);
      if (res.data.success) { setRows(res.data.data || []); setTotal(res.data.total || 0); }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTopup = async () => {
    if (!topupOf) return;
    const amt = Number(topupAmount);
    if (!amt || amt <= 0) { toast.error('กรุณาระบุจำนวนเงิน'); return; }
    try {
      setSubmitting(true);
      const res = await merchantApi.post(`/admin/deposit-orders/fee-wallet/${topupOf.id}/topup`, { amount: amt });
      if (res.data.success) { toast.success(`เติมเครดิต fee สำเร็จ (คงเหลือ ${fmt(res.data.data.balance)})`); setTopupOf(null); setTopupAmount(''); loadData(); }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เติมไม่สำเร็จ');
    } finally { setSubmitting(false); }
  };

  // โหลด ledger แบบแบ่งหน้า (append) — รายการเยอะแค่ไหนก็ไล่ดูได้ครบ
  const fetchLedger = async (merchantId: string, offset: number) => {
    try {
      setLedgerLoading(true);
      const res = await merchantApi.get(`/admin/deposit-orders/fee-wallet/${merchantId}?limit=20&offset=${offset}`);
      if (res.data.success) {
        setLedger((prev) => (offset === 0 ? res.data.data.ledger || [] : [...prev, ...(res.data.data.ledger || [])]));
        setLedgerTotal(res.data.data.ledger_total || 0);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลด ledger ไม่สำเร็จ');
    } finally { setLedgerLoading(false); }
  };

  const openLedger = async (m: FeeWalletRow) => {
    setLedgerOf(m); setLedger([]); setLedgerTotal(0);
    await fetchLedger(m.id, 0);
  };

  const columns: Column<FeeWalletRow>[] = [
    {
      key: 'merchant', header: 'Merchant',
      render: (m) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'var(--theme-primary-gradient)' }}>
            {m.name?.charAt(0)?.toUpperCase() || <FontAwesomeIcon icon={faBuilding} />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{m.name}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${m.p2p_enabled ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'}`}>
                {m.p2p_enabled ? 'P2P เปิด' : 'P2P ปิด'}
              </span>
            </div>
            <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{m.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'balance', header: 'เครดิต fee', width: '160px',
      render: (m) => (
        <div>
          <p className="font-bold text-sm tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmt(m.fee_balance)}</p>
          <p className="text-[10px] text-gray-400 dark:text-slate-500 tabular-nums">เติม {fmt(m.total_credit)} · หัก {fmt(m.total_debit)}</p>
        </div>
      ),
    },
    {
      // เรทที่หักจากเครดิต fee ตอนจับคู่ peer / ปิดใบถอน P2P เท่านั้น — เคส fallback เข้าบัญชีเว็บไม่เกี่ยวกับหน้านี้
      key: 'pct', header: 'อัตราหักเครดิต fee', width: '170px',
      render: (m) => {
        const srcTag = (isP2p?: boolean) => isP2p
          ? <span className="text-[9px] px-1 py-px rounded font-medium bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">P2P</span>
          : <span className="text-[9px] px-1 py-px rounded font-medium bg-gray-100 dark:bg-slate-700 text-gray-400 dark:text-slate-500" title="ยังไม่ตั้งเรท P2P — ใช้ตัวเลขเดียวกับ fee ปกติ แต่ยังหักจากเครดิต fee">ตาม fee ปกติ</span>;
        return (
          <div className="text-xs text-gray-600 dark:text-slate-300 tabular-nums space-y-0.5">
            <p className="flex items-center gap-1">ฝาก {m.payin_pct}% {srcTag(m.payin_pct_is_p2p)}</p>
            <p className="flex items-center gap-1">ถอน {m.payout_pct}% {srcTag(m.payout_pct_is_p2p)}</p>
          </div>
        );
      },
    },
    {
      key: 'actions', header: 'จัดการ', width: '180px',
      render: (m) => (
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={() => { setTopupOf(m); setTopupAmount(''); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors">
            <FontAwesomeIcon icon={faCoins} className="mr-1" />เติม fee
          </button>
          <button onClick={() => openLedger(m)}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors">
            <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1" />Ledger
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <UiPageHeader
        icon={faChartPie}
        title="P2P Overview"
        subtitle={`จัดการกระเป๋าค่าธรรมเนียม P2P ของ merchant • ${total.toLocaleString()} ราย`}
      />

      {/* หน้านี้ = เฉพาะเงินที่หักจากเครดิต fee — เคส fallback ไม่เกี่ยว (กันสับสน) */}
      <p className="mb-4 -mt-2 text-xs text-gray-400 dark:text-slate-500">
        <FontAwesomeIcon icon={faCircleInfo} className="mr-1" />
        หน้านี้แสดงเฉพาะเงินที่หักจากเครดิต fee (ตอนจับคู่ P2P ฝาก/ถอน) — กรณี deposit ตกเข้า<strong>บัญชีเว็บ</strong> คิด fee ปกติหักจากยอดฝากตอนเข้า Wallet ไม่หักเครดิต และถูกนับรวมกับ fee ปกติใน Fee Report แล้ว
      </p>

      <UiFilterPanel
        onSearch={() => { setSearch(searchInput); setCurrentPage(1); }}
        onReset={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
        loading={loading}
        actions={<UiButton variant="secondary" icon={faMagnifyingGlass} onClick={() => { setSearch(searchInput); setCurrentPage(1); }}>ค้นหา</UiButton>}
      >
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ค้นหา Merchant</label>
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setCurrentPage(1); } }}
            placeholder="ชื่อร้าน"
            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500" />
        </div>
      </UiFilterPanel>

      <div className="mt-4">
        <DataTable
          data={rows}
          columns={columns}
          loading={loading}
          serverPagination={{ currentPage, totalItems: total, pageSize, onPageChange: setCurrentPage, onPageSizeChange: setPageSize }}
        />
      </div>

      {/* เติม fee */}
      <UiModal
        open={!!topupOf}
        onClose={() => setTopupOf(null)}
        title="เติมเครดิตค่าธรรมเนียม P2P"
        icon={faCoins}
        iconColor="text-green-500"
        size="sm"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setTopupOf(null)} disabled={submitting} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton onClick={handleTopup} loading={submitting} className="flex-1 justify-center">เติมเครดิต</UiButton>
          </div>
        }
      >
        {topupOf && (
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">Merchant:</span><strong className="text-gray-800 dark:text-slate-100">{topupOf.name}</strong></div>
            <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">คงเหลือ:</span><strong className="tabular-nums text-green-600 dark:text-green-400">{fmt(topupOf.fee_balance)}</strong></div>
            <UiInput label="จำนวนเติม" required prefix="฿" type="number" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} placeholder="0.00" />
          </div>
        )}
      </UiModal>

      {/* ledger */}
      <UiModal open={!!ledgerOf} onClose={() => setLedgerOf(null)} title="ประวัติกระเป๋าค่าธรรมเนียม P2P" icon={faClockRotateLeft} iconColor="text-indigo-500" size="lg" scrollable>
        {ledgerOf && (
          <>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 mb-3 flex items-center justify-between text-sm">
              <strong className="text-gray-800 dark:text-slate-100">{ledgerOf.name}</strong>
              <strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmt(ledgerOf.fee_balance)}</strong>
            </div>
            {ledgerLoading && ledger.length === 0 ? (
              <div className="text-center py-8 text-gray-400 dark:text-slate-500">กำลังโหลด...</div>
            ) : ledger.length === 0 ? (
              <div className="text-center py-8 text-gray-400 dark:text-slate-500">ยังไม่มีรายการ</div>
            ) : (
              <div className="space-y-2">
                {ledger.map((l, i) => (
                  <div key={i} className="flex items-center justify-between border border-gray-100 dark:border-slate-700 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{LEDGER_TYPE[l.type] || l.type}</p>
                      <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{l.note || ''} · {new Date(l.created_at).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className={`text-sm font-bold tabular-nums ${l.direction === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        <FontAwesomeIcon icon={l.direction === 'credit' ? faArrowDown : faArrowUp} className="mr-1 text-[10px]" />
                        {l.direction === 'credit' ? '+' : '−'}{fmt(l.amount)}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 tabular-nums">คงเหลือ {fmt(l.balance_after)}</p>
                    </div>
                  </div>
                ))}
                {ledger.length < ledgerTotal && (
                  <button
                    type="button"
                    onClick={() => ledgerOf && fetchLedger(ledgerOf.id, ledger.length)}
                    disabled={ledgerLoading}
                    className="w-full py-2 rounded-lg text-xs font-medium border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    {ledgerLoading ? 'กำลังโหลด...' : `โหลดเพิ่มเติม (แสดง ${ledger.length}/${ledgerTotal})`}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </UiModal>
    </>
  );
};

export default AdminP2pOverviewPage;
